import React from "react";
import {
  MdAdd,
  MdCancel,
  MdAccessTime,
  MdHistory,
  MdPerson,
  MdRefresh,
} from "react-icons/md";
import Card from "components/card";
import { Trial } from "../../types/financial";
import {
  getEntitlementLabel,
  getTrialEntitlements,
} from "../../utils/trialEntitlements";
import EmptyState from "../common/EmptyState";
import LoadingSpinner from "../common/LoadingSpinner";
import { badge } from "../../utils/badgeColors";

interface UserTrialAccessCardProps {
  activeTrial: Trial | null;
  trialHistory: Trial[];
  loading: boolean;
  error: string | null;
  hasActiveSubscription: boolean;
  onGrantTrial: () => void;
  onRevokeTrial: (trial: Trial) => void;
  onRefresh: () => void;
}

const UserTrialAccessCard: React.FC<UserTrialAccessCardProps> = ({
  activeTrial,
  trialHistory,
  loading,
  error,
  hasActiveSubscription,
  onGrantTrial,
  onRevokeTrial,
  onRefresh,
}) => {
  const isGrantDisabled = !!activeTrial || hasActiveSubscription;
  const grantDisabledReason = hasActiveSubscription
    ? "Cannot grant trial while user has an active subscription"
    : activeTrial
    ? "User already has an active trial"
    : "";

  const getAdminDisplayName = (
    admin: { first_name: string; last_name: string } | null | undefined
  ): string => {
    if (!admin) {
      return "System";
    }
    return (
      `${admin.first_name || ""} ${admin.last_name || ""}`.trim() || "System"
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getTrialStatus = (trial: Trial) => {
    if (trial.revoked_at) {
      return {
        label: "Revoked",
        color: badge("red"),
      };
    }
    const now = new Date();
    const endsAt = new Date(trial.ends_at);
    if (endsAt < now) {
      return {
        label: "Expired",
        color: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
      };
    }
    return {
      label: "Active",
      color: badge("green"),
    };
  };

  const getRemainingDays = (endsAt: string) => {
    const now = new Date();
    const end = new Date(endsAt);
    const diff = end.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  const renderEntitlements = (trial: Trial) => (
    <div className="flex flex-wrap gap-1">
      {getTrialEntitlements(trial).map((entitlement) => (
        <span
          key={entitlement}
          className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800 dark:bg-blue-800 dark:text-blue-100"
        >
          {getEntitlementLabel(entitlement)}
        </span>
      ))}
    </div>
  );

  if (loading) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <LoadingSpinner />
      </Card>
    );
  }

  if (error) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <div className="py-12 text-center">
          <div className="mb-4 text-lg text-red-500">{error}</div>
          <button
            onClick={onRefresh}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <MdRefresh className="mr-2 h-4 w-4" />
            Retry
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card extra="w-full p-3 md:p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-bold text-navy-700 dark:text-white">
          Trial Access
        </h2>
        <div className="group relative">
          <button
            onClick={onGrantTrial}
            disabled={isGrantDisabled}
            className={`inline-flex items-center rounded-md bg-biafit-primary px-4 py-2 text-sm font-medium text-white shadow-sm ${
              isGrantDisabled
                ? "cursor-not-allowed opacity-25"
                : "hover:bg-biafit-secondary"
            }`}
          >
            <MdAdd className="mr-2 h-4 w-4" />
            Grant Trial
          </button>
          {isGrantDisabled && (
            <div className="pointer-events-none absolute bottom-full right-0 mb-2 hidden w-max max-w-xs rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover:block dark:bg-gray-700">
              {grantDisabledReason}
              <div className="border-x-transparent absolute right-4 top-full h-0 w-0 border-x-4 border-t-4 border-t-gray-900 dark:border-t-gray-700"></div>
            </div>
          )}
        </div>
      </div>

      {/* Active Trial Section */}
      {activeTrial ? (
        <div className="mb-6">
          <div className="rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-800">
                    <MdAccessTime className="h-6 w-6 text-green-600 dark:text-green-300" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                    Active Trial
                  </h3>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {activeTrial.trial_days} day
                    {activeTrial.trial_days !== 1 ? "s" : ""} trial
                  </p>
                </div>
              </div>
              <button
                onClick={() => onRevokeTrial(activeTrial)}
                className="inline-flex items-center rounded-md bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-200 dark:hover:bg-red-900"
              >
                <MdCancel className="mr-1 h-4 w-4" />
                Revoke
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-green-600 dark:text-green-400">
                  Started
                </p>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  {formatDate(activeTrial.starts_at)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-green-600 dark:text-green-400">
                  Expires
                </p>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  {formatDate(activeTrial.ends_at)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-green-600 dark:text-green-400">
                  Remaining
                </p>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  {getRemainingDays(activeTrial.ends_at)} day
                  {getRemainingDays(activeTrial.ends_at) !== 1 ? "s" : ""}
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-green-600 dark:text-green-400">
                  Entitlements
                </p>
                {renderEntitlements(activeTrial)}
              </div>
            </div>

            {/* Granted by info */}
            <div className="mt-4 border-t border-green-200 pt-4 dark:border-green-700">
              <div className="flex items-center text-sm text-green-600 dark:text-green-400">
                <MdPerson className="mr-1 h-4 w-4" />
                <span>
                  Granted by {getAdminDisplayName(activeTrial.granted_by)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <EmptyState
            icon={MdAccessTime}
            title="No Active Trial"
            description="This user does not have an active trial period."
            padding="sm"
          />
        </div>
      )}

      {/* Trial History Section */}
      {trialHistory.length > 0 && (
        <div>
          <div className="mb-4 flex items-center space-x-2">
            <MdHistory className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
              Trial History
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({trialHistory.length})
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-navy-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Period
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Granted By
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Entitlements
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-navy-900">
                {trialHistory.map((trial) => {
                  const status = getTrialStatus(trial);
                  return (
                    <tr key={trial.id}>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className="text-sm font-medium text-navy-700 dark:text-white">
                          {trial.trial_days} day
                          {trial.trial_days !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDateShort(trial.starts_at)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          to {formatDateShort(trial.ends_at)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {getAdminDisplayName(trial.granted_by)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDateShort(trial.created_at)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {renderEntitlements(trial)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${status.color}`}
                        >
                          {status.label}
                        </span>
                        {trial.revoked_at && (
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            by {getAdminDisplayName(trial.revoked_by)}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty history state */}
      {trialHistory.length === 0 && !activeTrial && (
        <div className="py-4 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No trial history available.
          </p>
        </div>
      )}
    </Card>
  );
};

export default UserTrialAccessCard;
